import { applyWhiteLabelTheme, LANGUAGE_TYPE, WHITE_LABEL_THEME, type WhiteLabelData } from "@web3auth/auth";
import Color from "color";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { loadLanguageAsync } from "@/plugins/i18n-setup";
import { DEFAULT_LOGO_DARK, DEFAULT_LOGO_LIGHT } from "@/utils/constants";
import { getUserLanguage } from "@/utils/utils";

const setThemeClass = (isDark: boolean) => {
  const htmlElement = document.querySelector("html") as HTMLElement;
  if (isDark) {
    htmlElement.classList.add("dark");
  } else {
    htmlElement.classList.remove("dark");
  }
};

const setThemeColors = (whiteLabelTheme: WHITE_LABEL_THEME | undefined) => {
  const rootElement = document.querySelector(":root") as HTMLElement;
  if (whiteLabelTheme) applyWhiteLabelTheme(Color, rootElement, whiteLabelTheme);
};

const setLanguage = (whiteLabelDefaultLanguage: LANGUAGE_TYPE | undefined) => {
  if (!whiteLabelDefaultLanguage) {
    loadLanguageAsync(getUserLanguage());
    return;
  }

  loadLanguageAsync(whiteLabelDefaultLanguage);
};

/**
 * Stores all the configurations of the dapp
 */
const useDappStore = defineStore("dapp", () => {
  const whiteLabel = ref<WhiteLabelData>();
  const isCustomVerifier = ref<boolean>(false);

  const isDarkMode = computed<boolean>(() => {
    let isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    if (whiteLabel.value?.mode) {
      isDark = whiteLabel.value.mode === "auto" ? isDark : whiteLabel.value.mode === "dark";
    }
    return isDark;
  });

  const whiteLabelLogo = computed(() => {
    return {
      light: whiteLabel.value?.logoLight ?? "",
      dark: whiteLabel.value?.logoDark ?? "",
    };
  });

  const useLogoLoader = computed<boolean>(() => {
    return Boolean(whiteLabel.value?.useLogoLoader);
  });

  const hasOwnLogo = computed(() => {
    const dappLogo = isDarkMode.value ? whiteLabelLogo.value?.dark : whiteLabelLogo.value?.light;
    return ![DEFAULT_LOGO_DARK, DEFAULT_LOGO_LIGHT].includes(dappLogo) && dappLogo !== "";
  });

  const showLogo = computed(() => {
    if (hasOwnLogo.value && (useLogoLoader.value || Boolean(isCustomVerifier.value))) {
      return true;
    }
    return false;
  });

  const hydrateDapp = (whiteLabelData: WhiteLabelData | undefined, customVerifier: boolean) => {
    whiteLabel.value = whiteLabelData;
    isCustomVerifier.value = customVerifier;
    setLanguage(whiteLabel.value?.defaultLanguage);
    setThemeClass(isDarkMode.value);
    setThemeColors(whiteLabel.value?.theme);
  };

  return { whiteLabel, isCustomVerifier, isDarkMode, whiteLabelLogo, showLogo, hydrateDapp };
});

export default useDappStore;
